import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Scrollbar } from '../../../../components/atoms/scrollbar';
import { VisitorMeili } from '../../api/types/visitor';
import { useVisitorsActionDialog } from '../../hooks/use-visitors-action-dialog';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog } from '@/components/molecules/dialog';
import { VisitorsTableRow } from '@/features/visitors/components/molecules/visitors-table-row';
import { useResendVisitorInvitation } from '@/features/visits/hooks/mutation/use-resend-visitor-invitation';
import { SetSortController } from '@/hooks/use-sort-controller';
import { SortOrder } from '@/types/enum/sort-order';
import { SortController } from '@/types/interfaces/sort-controller';

interface Props {
  visits: VisitorMeili[];
  isLoading: boolean;
  error: string;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
}

export const VisitorsTable = ({
  visits,
  error,
  sortController,
  handleChangeSort,
  setSortController,
  isLoading,
}: Props) => {
  const { t } = useTranslation(['visit', 'visits', 'common']);
  const {
    isOpen: isVisitorActionDialogOpen,
    openConfirmDialog,
    openRejectDialog,
    closeDialog,
    dialogConfig: visitorActionDialogConfig,
    isActionInProgress: isVisitorActionInProgress,
  } = useVisitorsActionDialog();

  const { mutate: resendVisitorInvitation } = useResendVisitorInvitation();

  const columns = [
    {
      id: 'visitor.firstName',
      label: t('visits:table.columns.guests'),
      sort: false,
    },
    {
      id: 'visitHostId',
      label: t('visits:table.columns.host'),
      sort: false,
    },
    {
      id: 'visitSource',
      label: t('visits:table.columns.source'),
      sort: false,
    },
    {
      id: 'confirmed',
      label: t('visits:table.columns.confirmed'),
      sort: false,
    },
    {
      id: 'category',
      label: t('visits:table.columns.category'),
      sort: false,
    },
    {
      id: 'visitDestinationId',
      label: t('visits:table.columns.destination'),
      sort: false,
    },
    {
      id: 'visitExpectedAt',
      label: t('visits:table.columns.expectedAt'),
      sort: true,
    },
    {
      id: 'visitState',
      label: t('visits:table.columns.visitState'),
      sort: false,
    },
    {
      id: 'state',
      label: t('visits:table.columns.visitorState'),
      sort: false,
    },
    {
      id: 'actions',
      label: '',
      sort: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.sort ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={true}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() => {
                        setSortController(prev => {
                          handleChangeSort([
                            `${column.id}:${
                              prev.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
                            }`,
                          ]);
                          return {
                            ...prev,
                            sortOrder:
                              prev.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC,
                            sortBy: column.id,
                          };
                        });
                      }}
                      key={column.id}>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {visits?.map(visitor => (
              <VisitorsTableRow
                key={visitor.id}
                visitor={visitor}
                isVisitorActionInProgress={isVisitorActionInProgress}
                openConfirmDialog={openConfirmDialog}
                openRejectDialog={openRejectDialog}
                resendVisitorInvitation={resendVisitorInvitation}
              />
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <Dialog
        open={isVisitorActionDialogOpen}
        onCancel={closeDialog}
        onConfirm={visitorActionDialogConfig?.onConfirm}
        title={visitorActionDialogConfig?.title ?? ''}
        variant={visitorActionDialogConfig?.variant}
        confirmButtonText={visitorActionDialogConfig?.confirmButtonText}
        cancelButtonText={t('common:button.close')}
        isConfirmButtonDisabled={!visitorActionDialogConfig}
        isConfirmButtonSubmitting={visitorActionDialogConfig?.isSubmitting}>
        <Typography sx={{ my: 2 }} variant="subtitle2">
          {visitorActionDialogConfig?.description}
        </Typography>
      </Dialog>
      <DataStatusIndicator dataLength={visits?.length} error={error} isFetching={isLoading} />
      <Divider sx={{ mt: 'auto' }} />
    </Box>
  );
};
